import type { Coding, QuestionnaireResponse, QuestionnaireResponseItemAnswer } from "@medplum/fhirtypes";
import type { MemberId, QuestionnaireAnswerContent } from "@repo/domain";
import { codeSystemUrl, QUESTIONNAIRE_OPTION_SYSTEM } from "@repo/fhir-systems";
import { omitUndefined } from "../engine/omit-undefined";
import { unitFromQuantity, unitToQuantity } from "../lib/ucum";
import { required } from "./read";

export const QUESTIONNAIRE_FREE_TEXT_SYSTEM = codeSystemUrl("questionnaire-free-text");

/** Shared answer values for questionnaire instances and performance measures. */
export function contentFromAnswer(
  row: QuestionnaireResponse,
  answer: QuestionnaireResponseItemAnswer,
): QuestionnaireAnswerContent | undefined {
  const coding = answer.valueCoding;
  if (coding) {
    return coding.system === QUESTIONNAIRE_FREE_TEXT_SYSTEM
      ? { kind: "Choice", freeText: required(row, "answers.freeText", coding.display) }
      : { kind: "Choice", optionId: required(row, "answers.optionId", coding.code) as MemberId };
  }
  if (answer.valueQuantity !== undefined) {
    return omitUndefined({
      kind: "Quantity" as const,
      value: required(row, "answers.value", answer.valueQuantity.value),
      unit: unitFromQuantity(answer.valueQuantity),
    });
  }
  if (answer.valueInteger !== undefined) return { kind: "Integer", value: answer.valueInteger };
  if (answer.valueBoolean !== undefined) return { kind: "Boolean", value: answer.valueBoolean };
  if (answer.valueDate !== undefined) return { kind: "Date", value: answer.valueDate };
  if (answer.valueUri !== undefined) return { kind: "Url", value: answer.valueUri };
  if (answer.valueAttachment !== undefined) {
    return omitUndefined({
      kind: "Attachment" as const,
      url: required(row, "answers.url", answer.valueAttachment.url),
      contentType: answer.valueAttachment.contentType,
      title: answer.valueAttachment.title,
    });
  }
  if (answer.valueString !== undefined) return { kind: "Text", value: answer.valueString };
  // An item with no value at all is the same as no item: sparse means absent,
  // and reading a blank back as an answer would make "untouched" unknowable.
  return undefined;
}

export function answerFromContent(value: QuestionnaireAnswerContent): QuestionnaireResponseItemAnswer {
  switch (value.kind) {
    case "Quantity":
      return { valueQuantity: omitUndefined({ value: value.value, ...unitToQuantity(value.unit) }) };
    case "Integer":
      return { valueInteger: value.value };
    case "Boolean":
      return { valueBoolean: value.value };
    case "Text":
      return { valueString: value.value };
    case "Date":
      return { valueDate: value.value };
    case "Url":
      return { valueUri: value.value };
    case "Attachment":
      return { valueAttachment: omitUndefined({ url: value.url, contentType: value.contentType, title: value.title }) };
    case "Choice":
      return { valueCoding: choiceCoding(value) };
  }
}

function choiceCoding(value: Extract<QuestionnaireAnswerContent, { kind: "Choice" }>): Coding {
  return value.freeText !== undefined
    ? { system: QUESTIONNAIRE_FREE_TEXT_SYSTEM, display: value.freeText }
    : { system: QUESTIONNAIRE_OPTION_SYSTEM, code: value.optionId };
}
